import type { ValueRange } from "@mm/api.meteomatics.com";
import { type RgbaColor, parseHexString } from "@mm/color-lib";

export type CustomColormapLike = {
  id?: string;
  label?: string;
  type?: string;
  stops: [number, string][];
};

export type CustomColormapSamples = {
  usualValueRange: ValueRange;
  samples: RgbaColor[];
};

const DEFAULT_SAMPLE_COUNT = 256;
const MAX_AUTO_SAMPLE_COUNT = 8192;
const AUTO_TEXELS_PER_NARROWEST_INTERVAL = 2;

type NormalizedStop = {
  value: number;
  color: RgbaColor;
};

/**
 * Normalize a colormap type to a supported value.
 * Defaults to "continuous" for missing/unknown inputs.
 */
const normalizeType = (type: string | undefined): "continuous" | "segmented" => {
  if (!type) {
    return "continuous";
  }
  const normalized = type.trim().toLowerCase();
  if (normalized === "segmented") {
    return "segmented";
  }
  if (normalized === "continuous") {
    return "continuous";
  }
  return "continuous";
};

/**
 * Parse a color string into a normalized RGBA tuple.
 * Accepts hex strings (via parseHexString) or rgb/rgba() CSS syntax.
 * RGB values > 1 are treated as 0-255; otherwise 0-1. Alpha is 0-1.
 */
const parseColor = (value: string, index: number): RgbaColor => {
  const trimmed = value.trim();
  const hex = parseHexString(trimmed);
  if (hex) {
    return hex;
  }

  const rgbaMatch = trimmed.replace(/\s+/g, "").match(/^rgba?\(([^)]+)\)$/i);
  if (!rgbaMatch) {
    throw new Error(`Stop ${index} has an invalid color: ${String(value)}.`);
  }

  const parts = rgbaMatch[1].split(",");
  if (parts.length !== 3 && parts.length !== 4) {
    throw new Error(`Stop ${index} has an invalid color: ${String(value)}.`);
  }

  const toUnit = (raw: string, channelIndex: number): number => {
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      throw new Error(`Stop ${index} has an invalid color: ${String(value)}.`);
    }
    if (channelIndex < 3) {
      if (n <= 1) {
        return Math.min(Math.max(n, 0), 1);
      }
      return Math.min(Math.max(n / 255, 0), 1);
    }
    return Math.min(Math.max(n, 0), 1);
  };

  const r = toUnit(parts[0], 0);
  const g = toUnit(parts[1], 1);
  const b = toUnit(parts[2], 2);
  const a = parts[3] == null ? 1 : toUnit(parts[3], 3);

  return [r, g, b, a];
};

/**
 * Validate and normalize the stops array, parsing colors and sorting by value.
 */
const normalizeStops = (stops: [number, string][]): NormalizedStop[] => {
  if (!Array.isArray(stops)) {
    throw new Error('Field "stops" must be an array.');
  }
  if (stops.length < 2) {
    throw new Error("At least two stops are required.");
  }

  const parsed = stops.map((entry, index) => {
    if (!Array.isArray(entry) || entry.length !== 2) {
      throw new Error(`Stop ${index} must be a tuple [value, color].`);
    }
    const [value, color] = entry;
    if (typeof value !== "number" || !Number.isFinite(value)) {
      throw new Error(`Stop ${index} has an invalid number: ${String(value)}.`);
    }
    if (typeof color !== "string") {
      throw new Error(`Stop ${index} has an invalid color: ${String(color)}.`);
    }
    return { value, color: parseColor(color, index) };
  });

  return parsed.sort((a, b) => a.value - b.value);
};

/**
 * Linearly interpolate between two numbers.
 */
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Linearly interpolate between two RGBA colors.
 */
const lerpColor = (a: RgbaColor, b: RgbaColor, t: number): RgbaColor => [
  lerp(a[0], b[0], t),
  lerp(a[1], b[1], t),
  lerp(a[2], b[2], t),
  lerp(a[3], b[3], t),
];

/**
 * Estimate a LUT size that preserves narrow stop intervals when the shader samples the map uniformly.
 * This is capped to keep GPU/CPU usage bounded.
 */
const estimateSampleCount = (stops: NormalizedStop[]): number => {
  const min = stops[0].value;
  const max = stops[stops.length - 1].value;
  const span = max - min;
  if (span <= 0) {
    return DEFAULT_SAMPLE_COUNT;
  }

  let minPositiveGap = Number.POSITIVE_INFINITY;
  for (let i = 0; i < stops.length - 1; i++) {
    const gap = stops[i + 1].value - stops[i].value;
    if (gap > 0 && gap < minPositiveGap) {
      minPositiveGap = gap;
    }
  }

  if (!Number.isFinite(minPositiveGap)) {
    return DEFAULT_SAMPLE_COUNT;
  }

  const requiredCount = Math.ceil((span / minPositiveGap) * AUTO_TEXELS_PER_NARROWEST_INTERVAL) + 1;
  return Math.min(MAX_AUTO_SAMPLE_COUNT, Math.max(DEFAULT_SAMPLE_COUNT, requiredCount));
};

export function customColormapToSamples(colormap: CustomColormapLike, sampleCount?: number): CustomColormapSamples {
  /**
   * Generate evenly spaced color samples for a custom colormap.
   * - Validates sampleCount and stops.
   * - Returns samples across the min/max stop range.
   * - "segmented" returns step colors; "continuous" interpolates between stops.
   */
  const type = normalizeType(colormap.type);
  const stops = normalizeStops(colormap.stops);
  const count = Math.round(sampleCount ?? estimateSampleCount(stops));
  if (!Number.isFinite(count) || count < 2) {
    throw new Error("sampleCount must be a finite number >= 2.");
  }

  const min = stops[0].value;
  const max = stops[stops.length - 1].value;
  const span = max - min;

  const samples: RgbaColor[] = new Array(count);

  let stopIdx = 0;
  for (let i = 0; i < count; i++) {
    const unit = count === 1 ? 0 : i / (count - 1);
    const value = min + unit * span;

    while (stopIdx + 1 < stops.length && value >= stops[stopIdx + 1].value) {
      stopIdx++;
    }

    if (stopIdx >= stops.length - 1) {
      samples[i] = stops[stops.length - 1].color;
      continue;
    }

    if (type === "segmented") {
      samples[i] = stops[stopIdx].color;
      continue;
    }

    const lower = stops[stopIdx];
    const upper = stops[stopIdx + 1];
    const denom = upper.value - lower.value;
    if (denom <= 0) {
      samples[i] = upper.color;
      continue;
    }
    const t = (value - lower.value) / denom;
    samples[i] = lerpColor(lower.color, upper.color, t);
  }

  return {
    usualValueRange: { lowerBound: min, upperBound: max },
    samples,
  };
}
